import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { IconBuilding, IconExternalLink, IconLogout } from '@tabler/icons-react';
import { apiService } from '../services/api';
import { TermsState } from '../types';

interface TermsModalProps {
    terms: TermsState;
    companyName?: string;
    onAccepted: (terms: TermsState) => void;
    onLogout: () => void;
    /** Given to an admin who works as the company's user, so the window is not a dead end for them. */
    onSwitchTenant?: () => void;
}

/**
 * Holds the company's client admin until they accept the current terms and
 * conditions: it cannot be closed, only confirmed or left by logging out.
 */
export const TermsModal: React.FC<TermsModalProps> = ({ terms, companyName, onAccepted, onLogout, onSwitchTenant }) => {
    const [agreed, setAgreed] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const confirm = async () => {
        if (!terms.version) return;
        setSaving(true);
        setError(null);
        const res = await apiService.acceptTerms(terms.version);
        if (res.success && res.data?.accepted) {
            onAccepted(res.data);
            return;
        }
        setError(res.message || 'Súhlas sa nepodarilo uložiť.');
        setSaving(false);
    };

    return (
        <Modal show centered backdrop="static" keyboard={false} className="modal-blur">
            <Modal.Header>
                <Modal.Title>Obchodné podmienky</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="text-secondary mb-3">
                    Pred ďalšou prácou vo Flowy je potrebné, aby ste
                    {companyName ? <> za spoločnosť <strong>{companyName}</strong></> : ''} potvrdili
                    súhlas s obchodnými podmienkami.
                </p>

                <ul className="list-unstyled mb-3">
                    {terms.documents.map((doc) => (
                        <li key={doc.url} className="mb-1">
                            <a href={doc.url} target="_blank" rel="noopener noreferrer" data-e2e="terms-document">
                                <IconExternalLink size={16} className="me-1"/>{doc.title}
                            </a>
                        </li>
                    ))}
                    <li><small className="text-secondary">verzia {terms.version}</small></li>
                </ul>

                {error && <div className="alert alert-danger py-2">{error}</div>}

                <label className="form-check mb-0">
                    <input
                        type="checkbox"
                        className="form-check-input"
                        checked={agreed}
                        disabled={saving}
                        onChange={(e) => setAgreed(e.target.checked)}
                        data-e2e="terms-agree"
                    />
                    <span className="form-check-label">
                        Oboznámil(a) som sa s týmito dokumentmi, v mene spoločnosti s nimi súhlasím a zaväzujem sa ich dodržiavať.
                    </span>
                </label>
            </Modal.Body>
            <Modal.Footer className="justify-content-between">
                <span className="d-flex gap-1">
                    <button type="button" className="btn btn-link text-secondary px-2 ms-n2" onClick={onLogout}>
                        <IconLogout size={14} className="me-1"/> Odhlásiť sa
                    </button>
                    {onSwitchTenant && (
                        <button type="button" className="btn btn-link text-secondary px-2" onClick={onSwitchTenant} data-e2e="terms-switch-tenant">
                            <IconBuilding size={14} className="me-1"/> Zmeniť zákazníka
                        </button>
                    )}
                </span>
                <button
                    type="button"
                    className="btn btn-primary btn-pill"
                    disabled={!agreed || saving}
                    onClick={confirm}
                    data-e2e="terms-accept"
                >
                    {saving && <span className="spinner-border spinner-border-sm me-2" role="status"/>}
                    Potvrdiť súhlas
                </button>
            </Modal.Footer>
        </Modal>
    );
};
